import "./helpers/publicPath";
import React, { useReducer } from "react";
import { createPortal } from "react-dom";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import { ThemeProvider, StyledEngineProvider } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import { theme } from "./theme";
import { PdpFlow, PdpFlowProps } from "./modules/PdpFlow";
import { NavView } from "./features/nav/NavView";
import FreeTrialFlow from "./features/trial/FreeTrialFlow";
import { RenderKind } from "./types/render-kind";
import TrialHeader from "./features/trial/TrialHeader";
import { CartProvider, BuyButton } from "./features/cart";
import BundleTrialFooter from "./features/trial/BundleTrialGlobal";
import { ProductSlider } from "./modules/ProductSlider";
import { RelatedProducts } from "./modules/BlogRelatedProducts";
import { ImageGridSlider as ImageGridSliderModule } from "./modules/ImageGridSlider";
import { PressModule } from "./modules/PressModule";
import { UserTestimonialModule } from "./modules/UserTestimonialModule";
import { initSentry } from "./helpers/errors";
import { BundlesCTA } from "./modules/BundlesCTA";
import { ProductDiscovery } from "./modules/ProductDiscovery";
import { LandingPageBanner } from "./modules/LandingPageBanner";
import { AccountPage } from "./features/account/AccountPage";
import {
  SubscriptionPortalRoot,
  RechargeAddress,
  RechargeItemRaw,
} from "./features/portal";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./helpers/fetch";
import { BundleModule } from "./modules/BundleModule";
import { BundleDrawerButtonHandler } from "./features/bundles/BundleDrawerButtonHandler";
import { Customer, CustomerProvider } from "./features/CustomerContext";
import { GrowthBookProvider } from "@growthbook/growthbook-react";
import { growthBook } from "./helpers/growthbook";
import { getProduct, trimPortalProps } from "./helpers/droppsApp";
import { initializeAnnouncementBar } from "./modules/announcement-bar";
import { initializeQuotesSliders } from "./modules/Quotes";
import { initNativeProductCards } from "./features/product/NativeProductCard";
export { getActiveTests } from "./helpers/growthbook";
export { setCartAndCheckout } from "./services/shopifyCart";
export { getUTMAttributes } from "./helpers/utm";
export { generateRechargeUrl } from "./features/portal/RechargeApiActions";
export { getProduct } from "./helpers/droppsApp";

initSentry();
initializeAnnouncementBar();
initializeQuotesSliders();
export interface SubManagementProps {
  subscriptions: RechargeItemRaw[];
  addresses: RechargeAddress[];
}

const CreateSubscriptionPortal = ({
  subscriptions,
  addresses,
}: SubManagementProps) => (
  <SubscriptionPortalRoot
    rawSubscriptions={subscriptions}
    addresses={addresses}
  />
);

const Pdp = ({
  product,
  scents,
  cartAddId,
  pdpMetaData,
  updateScentUrl,
}: PdpFlowProps) => {
  return (
    <PdpFlow
      product={product}
      scents={scents}
      cartAddId={cartAddId}
      pdpMetaData={pdpMetaData}
      updateScentUrl={updateScentUrl}
    />
  );
};

export interface TrialBundleFooterProps {
  link: string;
  maxNumItems: number;
}

const TrialBundleFooter = ({ link, maxNumItems }: TrialBundleFooterProps) => (
  <BundleTrialFooter shopViewLink={link} maxNumItems={maxNumItems} />
);

export const renders: ({ id: string } & RenderKind)[] = [];

if (
  typeof window !== "undefined" &&
  (window as unknown as { __reactRenderQueue?: unknown[] }).__reactRenderQueue
) {
  const queue = (window as unknown as { __reactRenderQueue: unknown[] })
    .__reactRenderQueue;
  (window as unknown as { __reactRenderQueue: unknown[] }).__reactRenderQueue =
    [];
  queue.forEach((item: unknown) => {
    const entry = item as {
      __pdp?: boolean;
      getProductArgs?: Parameters<typeof getProduct>[0];
      payload?: Record<string, unknown>;
    };
    if (entry?.__pdp && entry.getProductArgs != null && entry.payload) {
      const [product, scents] = getProduct(entry.getProductArgs);
      renders.push({ ...entry.payload, product, scents } as {
        id: string;
      } & RenderKind);
    } else if (entry && !entry.__pdp) {
      renders.push(item as { id: string } & RenderKind);
    }
  });
}

const PortalComponent = (render: RenderKind) => {
  switch (render.kind) {
    case "free-trial":
      return <FreeTrialFlow {...trimPortalProps(render)} />;
    case "free-trial-banner":
      return <TrialHeader {...trimPortalProps(render)} />;
    case "image-grid-slider":
      return <ImageGridSliderModule {...trimPortalProps(render)} />;
    case "nav-view":
      return <NavView {...trimPortalProps(render)} />;
    case "pdp":
      return <Pdp {...trimPortalProps(render)} />;
    case "press-testimonials":
      return <PressModule {...trimPortalProps(render)} />;
    case "product-slider":
      return (
        <ProductSlider
          showProductCardListCollection={true}
          {...trimPortalProps(render)}
        />
      );
    case "related-products":
      return <RelatedProducts {...trimPortalProps(render)} />;
    case "sub-portal":
      return <CreateSubscriptionPortal {...trimPortalProps(render)} />;
    case "trial-bundle-footer":
      return <TrialBundleFooter {...trimPortalProps(render)} />;
    case "user-testimonials":
      return <UserTestimonialModule {...trimPortalProps(render)} />;
    case "buy-button":
      return <BuyButton {...trimPortalProps(render)} />;
    case "bundle-drawer":
      return <BundleDrawerButtonHandler {...trimPortalProps(render)} />;
    case "bundles-cta":
      return <BundlesCTA {...trimPortalProps(render)} />;
    case "product-discovery":
      return <ProductDiscovery {...trimPortalProps(render)} />;
    case "landing-page-banner":
      return <LandingPageBanner {...trimPortalProps(render)} />;
    case "bundle-module":
      return <BundleModule {...trimPortalProps(render)} />;
    case "account-page":
      return <AccountPage {...trimPortalProps(render)} />;
    default:
      return <></>;
  }
};

const DroppsApp = () => {
  // Whenever someone pushes a new render, force react to re-render
  const [count, forceUpdate] = useReducer((x) => x + 1, 0);
  if (count === 0) {
    renders.push = (...args) => {
      const ret = Array.prototype.push.call(renders, ...args);
      forceUpdate();
      return ret;
    };
  }

  const mobile: boolean = useMediaQuery(
    theme.breakpoints.down(theme.breakpoints.values.lg)
  );

  return (
    <>
      {renders.map((x) => {
        const el = document.getElementById(x.id);
        if (!el) {
          throw new Error(`expected id ${x.id} to exist`);
        }
        const key = `${x.id}-${x.kind}`;
        return createPortal(
          <Sentry.ErrorBoundary showDialog>
            <PortalComponent {...x} />
          </Sentry.ErrorBoundary>,
          el,
          key
        );
      })}
    </>
  );
};

export const render = ({ user }: { user: Customer | null }) => {
  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      name: `${user.firstName} ${user.lastName}`,
    });
  }

  const container = document.getElementById("dropps-app-root");
  if (container === null) {
    throw new Error("dropps-app-root not found");
  }

  const root = createRoot(container);
  root.render(
    <React.StrictMode>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <QueryClientProvider client={queryClient}>
            <CustomerProvider customer={user}>
              <GrowthBookProvider growthbook={growthBook}>
                <CartProvider>
                  <DroppsApp />
                </CartProvider>
              </GrowthBookProvider>
            </CustomerProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    </React.StrictMode>
  );

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initNativeProductCards, {
      once: true,
    });
  } else {
    initNativeProductCards();
  }
};
